// Formatação de valores monetários
export const formatCurrency = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return 'R$ 0,00';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return 'R$ 0,00';
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

/** Moeda compacta para eixos de gráficos (ex.: R$ 1,2 mi, R$ 350 mil). */
export const formatCurrencyCompact = (value: number | string | null | undefined): string => {
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (value === null || value === undefined || isNaN(n)) return 'R$ 0';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1).replace('.', ',')} mi`;
  if (abs >= 1000) return `R$ ${Math.round(n / 1000)} mil`;
  return formatCurrency(n);
};

// Formatação de valores para input (sem símbolo da moeda)
export const formatCurrencyInput = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return '';
  
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

// Converter string formatada para número
export const parseCurrency = (value: string): number => {
  if (!value) return 0;
  
  // Remove todos os caracteres exceto números, vírgula e ponto
  const cleanValue = value.replace(/[^\d,.-]/g, '');
  
  // Substitui vírgula por ponto para conversão
  const normalizedValue = cleanValue.replace(',', '.');
  
  const numValue = parseFloat(normalizedValue);
  
  return isNaN(numValue) ? 0 : numValue;
};

// Máscara para input de valores monetários
export const currencyMask = (value: string): string => {
  if (!value) return '';
  
  // Remove tudo que não é número
  const numbers = value.replace(/\D/g, '');
  
  if (numbers === '') return '';
  
  // Converte para centavos
  const cents = numbers.padStart(3, '0');
  const reais = cents.slice(0, -2);
  const centavos = cents.slice(-2);
  
  // Formata
  if (reais === '0') {
    return `0,${centavos}`;
  }
  
  // Adiciona separadores de milhares
  const reaisFormatados = reais.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `${reaisFormatados},${centavos}`;
};

// Formatação de CNPJ
export const formatCNPJ = (value: string | null | undefined): string => {
  if (!value) return '-';
  
  // Remove tudo que não é número
  const numbers = value.replace(/\D/g, '');
  
  // Aplica máscara
  return numbers.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  );
};

// Formatação de telefone
export const formatPhone = (value: string | null | undefined): string => {
  if (!value) return '-';
  
  // Remove tudo que não é número
  const numbers = value.replace(/\D/g, '');
  
  // Aplica máscara baseada no tamanho
  if (numbers.length === 11) {
    return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  } else if (numbers.length === 10) {
    return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  
  return numbers;
};

// Formatação de data
export const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  
  let date: Date;
  
  if (typeof value === 'string') {
    // Se a string contém apenas a data (YYYY-MM-DD), adicionar horário para evitar problemas de timezone
    if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      date = new Date(value + 'T00:00:00');
    } else if (value.includes('T') && value.endsWith('Z')) {
      // Para datas ISO com timezone UTC, ajustar para o fuso horário local
      const isoDate = new Date(value);
      // Se a data UTC é um dia diferente da data local, usar a data local
      if (isoDate.getUTCDate() !== isoDate.getDate()) {
        // Criar uma nova data usando apenas a parte da data (YYYY-MM-DD) menos um dia
        const dateOnly = value.split('T')[0];
        const [year, month, day] = dateOnly.split('-').map(Number);
        const localDate = new Date(year, month - 1, day - 1);
        date = localDate;
      } else {
        date = isoDate;
      }
    } else {
      date = new Date(value);
    }
  } else {
    date = value;
  }
  
  if (isNaN(date.getTime())) return '';
  
  return new Intl.DateTimeFormat('pt-BR').format(date);
};

// Formatação de data e hora
export const formatDateTime = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  
  const date = typeof value === 'string' ? new Date(value) : value;
  
  if (isNaN(date.getTime())) return '';
  
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

// Formatação de data para input datetime-local
export const formatDateForInput = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  
  const date = typeof value === 'string' ? new Date(value) : value;
  
  if (isNaN(date.getTime())) return '';
  
  // Formato YYYY-MM-DDTHH:mm
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Formatação de CPF
export const formatCPF = (value: string | null | undefined): string => {
  if (!value) return '-';
  
  // Remove tudo que não é número
  const numbers = value.replace(/\D/g, '');
  
  // Aplica máscara
  return numbers.replace(
    /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
    '$1.$2.$3-$4'
  );
};

// Formatação de CEP
export const formatCEP = (value: string | null | undefined): string => {
  if (!value) return '-';
  
  // Remove tudo que não é número
  const numbers = value.replace(/\D/g, '');
  
  // Aplica máscara
  return numbers.replace(
    /^(\d{5})(\d{3})$/,
    '$1-$2'
  );
};

// Formatação de porcentagem
export const formatPercentage = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '0%';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return '0%';
  
  return `${numValue.toFixed(2)}%`;
};

// Formatação de números grandes
export const formatNumber = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '0';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return '0';
  
  return new Intl.NumberFormat('pt-BR').format(numValue);
};

// Formatação de status
export const formatStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    'ABERTO': 'Aberto',
    'FECHADO': 'Fechado',
    'PARCIAL': 'Parcial',
    'VENCIDO': 'Vencido',
    'CONTATO': 'Contato',
    'NEGOCIACAO': 'Negociação',
    'PROMESSA': 'Promessa',
    'OUTROS': 'Outros',
  };
  
  return statusMap[status] || status;
};

// Formatação de função do usuário
export const formatUserRole = (role: string): string => {
  const roleMap: Record<string, string> = {
    'ADMIN': 'Administrador',
    'GESTOR': 'Gestor',
    'OPERADOR': 'Operador',
    'OFICINA': 'Oficina',
  };
  
  return roleMap[role] || role;
};
